import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { QuizMode } from '@/types';

export type QuizLength = 20 | 50 | 100 | 'all';

export interface AchievementsState {
  // Track perfect scores per mode per length
  perfectScores: Record<QuizMode, Record<string, boolean>>;
}

interface AchievementsActions {
  recordPerfectScore: (mode: QuizMode, length: QuizLength) => void;
  hasPerfectScore: (mode: QuizMode, length: QuizLength) => boolean;
  isLengthUnlocked: (mode: QuizMode, length: QuizLength) => boolean;
  getUnlockedLengths: (mode: QuizMode) => QuizLength[];
  getAchievements: () => { mode: QuizMode; length: QuizLength }[];
  reset: () => void;
}

type AchievementsStore = AchievementsState & AchievementsActions;

const QUIZ_LENGTHS: QuizLength[] = [20, 50, 100, 'all'];

const initialState: AchievementsState = {
  perfectScores: {
    'flag-to-name-mc': {},
    'flag-to-name-type': {},
    'name-to-flag-mc': {},
  },
};

export const useAchievementsStore = create<AchievementsStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      recordPerfectScore: (mode, length) => {
        set((state) => ({
          perfectScores: {
            ...state.perfectScores,
            [mode]: {
              ...state.perfectScores[mode],
              [String(length)]: true,
            },
          },
        }));
      },

      hasPerfectScore: (mode, length) => {
        return get().perfectScores[mode]?.[String(length)] ?? false;
      },

      isLengthUnlocked: (mode, length) => {
        // 20 is always unlocked
        if (length === 20) return true;

        const modeScores = get().perfectScores[mode] ?? {};
        const lengthIndex = QUIZ_LENGTHS.indexOf(length);

        // Check if all previous lengths have perfect scores
        for (let i = 0; i < lengthIndex; i++) {
          const prevLength = QUIZ_LENGTHS[i];
          if (!modeScores[String(prevLength)]) {
            return false;
          }
        }
        return true;
      },

      getUnlockedLengths: (mode) => {
        return QUIZ_LENGTHS.filter((length) => get().isLengthUnlocked(mode, length));
      },

      getAchievements: () => {
        const achievements: { mode: QuizMode; length: QuizLength }[] = [];
        const { perfectScores } = get();

        for (const mode of Object.keys(perfectScores) as QuizMode[]) {
          for (const lengthStr of Object.keys(perfectScores[mode])) {
            if (perfectScores[mode][lengthStr]) {
              const length = lengthStr === 'all' ? 'all' : (Number(lengthStr) as 20 | 50 | 100);
              achievements.push({ mode, length });
            }
          }
        }

        return achievements;
      },

      reset: () => set(initialState),
    }),
    {
      name: 'flag-trainer-achievements',
      version: 1,
    }
  )
);
